import { createSelector } from "reselect";

import { HashMap } from "../../shared/model/HashMap";
import { FilterCategory } from "./ListState";
import { ProcessAggregateCount } from "./ProcessAggregateCount";
import { ProcessCountsModel } from "./ProcessCountsModel";
import { State } from "./State";

import * as _ from "lodash";
import Time from "shared/Time";

const getCurrentAgentGroup = (state: State) => state.currentAgentGroupName;
const getCounts = (state: State) => state.processCounts;
const getCountsTimeLimitDays = (state: State) => state.listState.countsTimeLimitDays;
export const getExecutionFilter = (filterCategory: string | null, filterText: string | null) => {
    if (filterCategory === FilterCategory.ErrorsSince ||
        filterCategory === FilterCategory.ExecutionsSince) {
        if (filterText != null) {
            const days = parseInt(filterText as string, 10);
            if (_.isNaN(days)) {
                return null;
            }
            return Time.add(new Date(), { days: -days }).toISOString();
        }
    }
    return null;
};

export const getAggregateCountsForCurrentAgentGroup = createSelector(
    [getCurrentAgentGroup, getCounts, getCountsTimeLimitDays, (state: State, executionFilterTimestamp: string | null) => executionFilterTimestamp],
    (agentGroupName, counts, countsTimeLimitDays, executionFilterTimestamp) => {
        const aggregatesPerProcess: HashMap<ProcessAggregateCount> = {};
        if (counts) {
            const minTimestampForData = Time.add(new Date(), { days: countsTimeLimitDays !== 1 ? -countsTimeLimitDays : -2 }).toISOString();
            const countsForEnv = counts[agentGroupName];
            if (countsForEnv) {
                for (const processGuid of Object.keys(countsForEnv)) {
                    const sortedCountPairs = _.toPairs(countsForEnv[processGuid]).sort((a, b) => a[0].localeCompare(b[0]));
                    const countsForTimeLimit = countsTimeLimitDays === 0 ? sortedCountPairs : sortedCountPairs.filter((v) => v[0] > minTimestampForData);
                    const filteredCounts = executionFilterTimestamp == null ? countsForTimeLimit : countsForTimeLimit.filter((v) => v[0] > executionFilterTimestamp);
                    const countsForProcess = filteredCounts.map((v) => v[1]);
                    aggregatesPerProcess[processGuid] = groupProcessCounts(agentGroupName, agentGroupName, processGuid, countsForProcess);
                    aggregatesPerProcess[processGuid].executionsPerDay = getCountSeries(countsTimeLimitDays || 60, countsForEnv[processGuid]);
                }
            }
        }
        return aggregatesPerProcess;
    }
);

function groupProcessCounts(environmentName: string, agentGroupName: string, processGuid: string, data: ProcessCountsModel[]): ProcessAggregateCount {
    // use an empty counts object as the starting point, as we calculate everything together
    const emptyExecutionCounts = getEmptyProcessExecutionCount(environmentName, processGuid);
    return _.reduce(data, (memo: ProcessAggregateCount, elem: ProcessCountsModel) => {
        memo.erroredNotAcknowledged = memo.erroredNotAcknowledged + (elem.erroredNotAcknowledged || 0);

        memo.running = memo.running + (elem.running || 0);
        if (elem.latestRunning) {
            memo.latestRunning = elem.latestRunning;
        }

        memo.failed = memo.failed + (elem.failed || 0);
        if (elem.latestFailed) {
            memo.latestFailed = elem.latestFailed;
        }

        memo.successful = memo.successful + (elem.successful || 0);
        if (elem.latestSuccessful) {
            memo.latestSuccessful = elem.latestSuccessful;
        }

        if (elem.latestExecutionState) {
            memo.latestExecutionState = elem.latestExecutionState;
        }

        const latestStartTime = _.max([elem.latestFailed, elem.latestSuccessful, elem.latestRunning]);
        if (latestStartTime) {
            memo.latestExecutionStartTime = latestStartTime;
        }

        memo.total = memo.running + memo.failed + memo.successful;

        return memo;
    }, emptyExecutionCounts);
}

function getEmptyProcessExecutionCount(environmentName: string, processGuid: string): ProcessAggregateCount {
    return {
        running: 0,
        failed: 0,
        erroredNotAcknowledged: 0,
        successful: 0,
        total: 0,
        executionsPerDay: []
    };
}

function getCountSeries(days: number, data: { [date: string]: ProcessCountsModel }): number[] {
    const series: number[] = [];
    let startDate = Time.add(new Date(), { days: -1 - days });
    let hasValues = false;
    for (let i = 0; i <= days; i++) {
        startDate = Time.add(startDate, { days: 1 });
        const dateString = Time.format(startDate, Time.dateFormat);
        const entryForDay = data[dateString] || {};
        const totalsForDay = (entryForDay.running || 0) + (entryForDay.successful || 0) + (entryForDay.failed || 0);
        if (totalsForDay > 0) {
            hasValues = true;
        }
        series.push(totalsForDay);
    }

    return hasValues ? series : [];
}
